import React from "react";
import { useAuth } from "./auth/AuthContext";
import { useHistory } from "react-router-dom";
import { useTranslation } from "../hooks/useTranslation";
import "./Paywall.css";

/**
 * Paywall Component - Redesigned Premium Look
 *
 * Modern split-layout design with:
 * - Premium dark left panel with icon and messaging
 * - Clean white right panel with benefits and CTA
 */
const Paywall = ({ title, message, onClose }) => {
  const t = useTranslation();
  const { currentUser } = useAuth();
  const history = useHistory();

  const defaultTitle = title || t("paywall.title", "Unlock Premium");
  const defaultMessage =
    message ||
    t(
      "paywall.defaultMessage",
      "Get full access to all premium content and features to accelerate your credit building journey."
    );

  const handleSubscribe = () => {
    if (onClose) {
      onClose();
    }
    history.push("/manage-subscription");
  };

  const handleLogin = () => {
    if (onClose) {
      onClose();
    }
    history.push("/login");
  };

  const benefits = [
    {
      icon: "📚",
      title: t("paywall.benefit1Title", "Full Content Access"),
      desc: t("paywall.benefit1Desc", "Unlock all premium articles and guides"),
    },
    {
      icon: "🤖",
      title: t("paywall.benefit2Title", "AI Credit Assistant"),
      desc: t("paywall.benefit2Desc", "Get personalized credit planning help"),
    },
    {
      icon: "💳",
      title: t("paywall.benefit3Title", "Card Inventory"),
      desc: t("paywall.benefit3Desc", "Track and manage your credit cards"),
    },
    {
      icon: "📊",
      title: t("paywall.benefit4Title", "Credit Report Parser"),
      desc: t("paywall.benefit4Desc", "Analyze your credit reports instantly"),
    },
  ];

  return (
    <div className="paywall-container">
      <div className="paywall-split">
        {/* Left Panel - Premium Visual */}
        <div className="paywall-left">
          <div className="paywall-left-content">
            <div className="paywall-badge">
              <span className="badge-icon">⭐</span>
              {t("paywall.premiumBadge", "PREMIUM")}
            </div>
            <div className="paywall-lock-icon">🔒</div>
            <h1 className="paywall-title">{defaultTitle}</h1>
            <p className="paywall-subtitle">{defaultMessage}</p>

            {/* Trust badges */}
            <div className="paywall-trust-badges">
              <div className="trust-badge">
                <span>🛡️</span>
                <span>{t("paywall.securePayment", "Secure")}</span>
              </div>
              <div className="trust-badge">
                <span>↩️</span>
                <span>{t("paywall.moneyBack", "7-day refund")}</span>
              </div>
              <div className="trust-badge">
                <span>✓</span>
                <span>{t("paywall.cancelAnytime", "Cancel anytime")}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Right Panel - Benefits & CTA */}
        <div className="paywall-right">
          <div className="paywall-right-content">
            <h2 className="benefits-heading">
              {t("paywall.whatYouGet", "What You'll Get")}
            </h2>

            <div className="benefits-grid">
              {benefits.map((benefit, idx) => (
                <div key={idx} className="benefit-card">
                  <span className="benefit-emoji">{benefit.icon}</span>
                  <div className="benefit-text">
                    <h4>{benefit.title}</h4>
                    <p>{benefit.desc}</p>
                  </div>
                </div>
              ))}
            </div>

            {/* Starting Price */}
            <div className="paywall-pricing-hint">
              <span className="price-label">
                {t("paywall.startingAt", "Starting at")}
              </span>
              <span className="price-value">$19</span>
              <span className="price-period">
                /{t("paywall.month", "month")}
              </span>
            </div>

            {/* CTA Buttons */}
            <div className="paywall-cta">
              <button className="paywall-btn-primary" onClick={handleSubscribe}>
                {t("paywall.viewPlans", "View Plans")}
                <span className="btn-arrow">→</span>
              </button>

              {!currentUser && (
                <button className="paywall-btn-secondary" onClick={handleLogin}>
                  {t("paywall.alreadyMember", "Already a member? Sign in")}
                </button>
              )}
            </div>

            {/* Guarantee */}
            <p className="paywall-guarantee">
              {t(
                "paywall.guaranteeText",
                "7-day money-back guarantee. No questions asked."
              )}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Paywall;
